import React from "react";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { Platform } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import styled from "styled-components/native";
import { AdsBannerProps, BannerActionEnum } from "./types";
import CustomWeb from "@components/atoms/Webview";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";
import { EventType } from "@utils/analytics";
import { createAdProps } from "./utils";
import { VDBIcons } from "@utils/VDBIcons";
import { css } from "styled-components/native";
import { useGlobalData } from "@redux/slices/globalSlice";
import { parseAndNavigate } from "@utils/product/searchResultsNavigation";
import { DefaultTheme } from "styled-components/native";

const AdsBanner: React.FC<AdsBannerProps> = ({ adsData, onClose }) => {
  const theme = useTheme();
  const { trackAdEvent } = useUserAnalytics() as any;
  const { products } = useGlobalData();
  const insets = useSafeAreaInsets();
  const handleAdAction = (action: BannerActionEnum) => {
    trackAdEvent(EventType.VIEW_BANNER, createAdProps(action, adsData));
    onClose();
  };

  trackAdEvent(
    EventType.VIEW_BANNER,
    createAdProps(BannerActionEnum.view, adsData)
  );
  const showAdsDetail = async () => {
    if (adsData.media_redirect_link_v2) {
      const url = adsData.media_redirect_link_v2.toString();
      trackAdEvent(
        EventType.VIEW_BANNER,
        createAdProps(BannerActionEnum.clickThrough, adsData)
      );

      try {
        if (url) {
          await parseAndNavigate(url, products);
          if (Platform.OS !== "web") {
            onClose();
          }
        }
      } catch (err) {
        console.error("An error occurred", err);
      }
    }
  };

  const renderCloseButton = () => (
    <ClosedView
      onPress={() => handleAdAction(BannerActionEnum.cancel)}
      insets={insets}
    >
      <ClosedContainerView>
        <VDBIcon
          name={VDBIcons.CLOSE_CIRCLE}
          size={theme.isMobile ? 24 : 33}
          color={theme["over-04"]}
          variant={IconsVariant.VDBCustom}
        />
      </ClosedContainerView>
    </ClosedView>
  );

  const renderWebCloseButton = () => (
    <ClosedViewWeb onPress={() => handleAdAction(BannerActionEnum.cancel)}>
      <ClosedContainerViewWeb>
        <VDBIcon
          name={VDBIcons.CLOSE_CIRCLE}
          size={24}
          color={theme["over-04"]}
          variant={IconsVariant.VDBCustom}
        />
      </ClosedContainerViewWeb>
    </ClosedViewWeb>
  );

  return (
    <Wrapper>
      {!theme.isLargeScreen && renderCloseButton()}
      <Container>
        <ContainerView>
          {theme.isLargeScreen && renderWebCloseButton()}
          <MediaContainer>
            {adsData.media_type === "image" ? (
              <AdImageTouchable
                onPress={showAdsDetail}
                disabled={!adsData.media_redirect_link_v2}
              >
                <AdImage
                  source={{ uri: adsData.media_url_url }}
                  resizeMode="contain"
                />
              </AdImageTouchable>
            ) : (
              <WebViewContainer onPress={showAdsDetail} style={{ flex: 1 }}>
                <CustomWeb url={adsData.media_url_url} style={{ flex: 1 }} />
              </WebViewContainer>
            )}
          </MediaContainer>
        </ContainerView>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled(SafeAreaView)`
  flex: 1;
  justify-content: center;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["over-01"]};
`;

const WebViewContainer = styled.TouchableOpacity`
  width: 100%;
  height: 100%;
`;

const Container = styled.View`
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? 12 : 24}px;
  box-sizing: border-box;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile &&
    `
    max-height: 680px;
  `}
`;

const ContainerView = styled.View`
  flex: 1;
  margin-top: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? 8 : 32}px;
  margin-bottom: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? 8 : 32}px;
  max-width: 720px;
  width: 100%;
  align-self: center;
`;

const ClosedView = styled.TouchableOpacity<any>`
  position: absolute;
  top: ${({ insets }: { insets: any }) =>
    insets?.top > 0 ? insets?.top + 4 : 16}px;
  right: 12;
  z-index: 1;
  padding: ${SpaceType.Basic}px;
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen &&
    css`
      padding: ${SpaceType.Double}px;
    `}
`;

const ClosedContainerView = styled.View`
  padding: ${SpaceType.Basic}px;
  border-radius: 40px;
`;

const ClosedViewWeb = styled.TouchableOpacity<any>`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  width: 72px;
  height: 72px;
  justify-content: center;
  align-items: center;
`;

const ClosedContainerViewWeb = styled.View`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["over-01"]};
  justify-content: center;
  align-items: center;
`;

const MediaContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
`;

const AdImageTouchable = styled.TouchableOpacity`
  flex: 1;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
`;

const AdImage = styled.Image`
  width: 100%;
  height: 100%;
  border-radius: 12px;
`;

export default AdsBanner;
